import {stripDigits} from './formatCardNumber.ts';
import {CreditCardScheme} from './types.ts';

const FOUR_DIGIT_PREFIX_LENGTH = 4;
const TWO_DIGIT_PREFIX_LENGTH = 2;

const AMEX_PREFIXES = ['34', '37'] as const;
const DINERS_PREFIX = '36';
const VISA_PREFIX = '4';

const JCB_FOUR_DIGIT_MIN = 3528;
const JCB_FOUR_DIGIT_MAX = 3589;

const MASTERCARD_FOUR_DIGIT_MIN = 2221;
const MASTERCARD_FOUR_DIGIT_MAX = 2720;
const MASTERCARD_TWO_DIGIT_MIN = 51;
const MASTERCARD_TWO_DIGIT_MAX = 55;

function fourDigitPrefix(digits: string): number | null {
    if (digits.length < FOUR_DIGIT_PREFIX_LENGTH) {
        return null;
    }
    return parseInt(digits.slice(0, FOUR_DIGIT_PREFIX_LENGTH), 10);
}

function twoDigitPrefix(digits: string): number | null {
    if (digits.length < TWO_DIGIT_PREFIX_LENGTH) {
        return null;
    }
    return parseInt(digits.slice(0, TWO_DIGIT_PREFIX_LENGTH), 10);
}

function matchScheme(input: string): CreditCardScheme | null {
    const digits = stripDigits(input);
    if (!digits) {
        return null;
    }

    if (AMEX_PREFIXES.some((prefix) => digits.startsWith(prefix))) {
        return CreditCardScheme.AMEX;
    }

    const four = fourDigitPrefix(digits);
    if (four !== null && four >= JCB_FOUR_DIGIT_MIN && four <= JCB_FOUR_DIGIT_MAX) {
        return CreditCardScheme.JCB;
    }

    if (digits.startsWith(DINERS_PREFIX)) {
        return CreditCardScheme.DINERS;
    }

    if (four !== null && four >= MASTERCARD_FOUR_DIGIT_MIN && four <= MASTERCARD_FOUR_DIGIT_MAX) {
        return CreditCardScheme.MASTERCARD;
    }

    const two = twoDigitPrefix(digits);
    if (two !== null && two >= MASTERCARD_TWO_DIGIT_MIN && two <= MASTERCARD_TWO_DIGIT_MAX) {
        return CreditCardScheme.MASTERCARD;
    }

    if (digits.startsWith(VISA_PREFIX)) {
        return CreditCardScheme.VISA;
    }

    return null;
}

export function detectSchemeFromCreditCardNumber(input: string): CreditCardScheme {
    return matchScheme(input) ?? CreditCardScheme.VISA;
}

export function isRecognisedCreditCardPrefix(input: string): boolean {
    return matchScheme(input) !== null;
}

export function getCreditCardDisplayNameByPrefix(prefix: string | null | undefined): string {
    const scheme = detectSchemeFromCreditCardNumber(prefix ?? "");
    switch (scheme) {
        case CreditCardScheme.VISA:
            return 'Visa';
        case CreditCardScheme.MASTERCARD:
            return 'Mastercard';
        case CreditCardScheme.AMEX:
            return 'American Express';
        case CreditCardScheme.DINERS:
            return 'Diners Club';
        case CreditCardScheme.JCB:
            return 'JCB';
        default: {
            const exhaustiveCheck: never = scheme;
            return exhaustiveCheck;
        }
    }
}
